import { useTheme } from '@mui/material';
import {
  PRE_LITS_PLACEHOLDER_PLATFORM_SLA_ID,
  PRE_LITS_PLACEHOLDER_SERVICE_LEVEL,
} from '@provision/service-level-utils';
import { useMemo } from 'react';
import { FormAutocompleteProps } from 'src/components';
import { CardCustomAttributeRows } from 'src/components/CustomAttributes/use-card-custom-attribute-rows';
import { AccountManagerSelectMenuField } from 'src/components/SidePanelTabs/account-manager-select-menu-field';
import { ProjectManagerSelectMenuField } from 'src/components/SidePanelTabs/Projects/project-manager-select-menu-field';
import { ServiceLevelSelectMenuField } from 'src/components/SidePanelTabs/service-level-select-menu-field';
import { componentsFontSizes } from 'src/constants/layout';
import {
  Address,
  formatServiceLevelWithCpp,
  getServiceLevelValue,
  Project,
  useAddress,
  useGroupRules,
  useProject,
  UserGroupRules,
  useServiceLevels,
} from 'src/graphql';
import { getCorrectSubscriptionIdFromAddress } from 'src/graphql/entities/address/utils';
import { usePlatformUsersOptions } from 'src/graphql/entities/platform-user';
import {
  useSectionVisibility,
  useSidePanelSectionOrder,
} from 'src/graphql/entities/side-panel-personalization/context';
import { SITE_CARD_PROPERTY_KEYS } from 'src/graphql/entities/side-panel-personalization/model/card-keys';
import { ConditionInput } from 'src/graphql/types';

export interface AssociatedSiteFormValues {
  serviceLevel: { id: string; label: string };
  accountManager: { id: string; label: string };
}

const activeUserFilters: ConditionInput[] = [
  {
    attribute: 'isActive',
    operator: '=',
    value: 'true',
  },
];

const NO_CUSTOM_ATTRIBUTE_ROWS: CardCustomAttributeRows = {};

export function useAssociatedSiteForm(
  address?: Partial<Address>,
  disabled = false,
  {
    project,
    customAttributeRows = NO_CUSTOM_ATTRIBUTE_ROWS,
  }: {
    project?: Partial<Project>;
    /** card-rendered rows the form places by the layout order, like its own fields */
    customAttributeRows?: CardCustomAttributeRows;
  } = {},
) {
  const theme = useTheme();
  const { updateAddress } = useAddress(address?.addressId);
  const { updateProject } = useProject(project?.id);

  const userFilters = useMemo(() => {
    const filters = [...activeUserFilters];

    if (address?.ownerId) {
      filters.push({
        attribute: 'userId',
        operator: '=',
        value: address?.ownerId,
      });
    }

    return filters;
  }, [address?.ownerId]);

  const projectManagerUserFilters = useMemo(() => {
    const filters = [...activeUserFilters];

    if (project?.ownerId) {
      filters.push({
        attribute: 'userId',
        operator: '=',
        value: project?.ownerId,
      });
    }

    return filters;
  }, [project?.ownerId]);

  const { allUserRecords: currentUsers } = usePlatformUsersOptions(
    {
      partnerId: address?.partnerId,
      shouldIncludePlatformPartnerId: false,
      limit: 1,
      filters: userFilters,
    },
    {
      skip: !address?.ownerId,
    },
  );

  const { allUserRecords: currentProjectManagerUsers } = usePlatformUsersOptions(
    {
      partnerId: project?.partnerId,
      limit: 1,
      filters: projectManagerUserFilters,
    },
    {
      skip: !project?.ownerId,
    },
  );

  const currentAccountManager = useMemo(() => currentUsers[0], [currentUsers]);
  const currentProjectManager = useMemo(
    () => currentProjectManagerUsers[0],
    [currentProjectManagerUsers],
  );

  const { checkValidity } = useGroupRules();

  const canUpdateSLA = checkValidity([UserGroupRules.CanUpdateSLAs]);

  // side-panel personalization: each field carries its own visibility, so the
  // consuming cards render the fields as-is (null layout = no provider = shown)
  const isSiteFieldVisible = useSectionVisibility('site');
  const siteFieldOrder = useSidePanelSectionOrder('site');

  const {
    serviceLevelOptions,
    loading: loadingServiceLevels,
    hasServiceLevelAccess,
  } = useServiceLevels(address?.partner?.id ?? address?.partnerId);

  const currentServiceLevel = useMemo(() => {
    const effectivePlatformSlaId = getServiceLevelValue({
      platformSlaId: address?.platformSlaId,
      partner: address?.partner,
      timeZone: address?.partner?.timeZone!,
    });

    if (effectivePlatformSlaId === undefined) {
      return null;
    }

    if (effectivePlatformSlaId === PRE_LITS_PLACEHOLDER_PLATFORM_SLA_ID) {
      return {
        id: PRE_LITS_PLACEHOLDER_SERVICE_LEVEL,
        label: PRE_LITS_PLACEHOLDER_SERVICE_LEVEL,
      };
    }

    const displayName = effectivePlatformSlaId
      ? serviceLevelOptions.find((option) => option.platformSlaId === effectivePlatformSlaId)
          ?.label
      : undefined;
    const label = formatServiceLevelWithCpp(displayName, address?.cppExpDate);

    return { id: label, label };
  }, [address?.platformSlaId, address?.cppExpDate, address?.partner, serviceLevelOptions]);

  const addressSubscriptionId = useMemo(
    () => getCorrectSubscriptionIdFromAddress(address as Address),
    [address],
  );

  const commonProps = useMemo(
    () => ({
      autocompleteProps: {
        forcePopupIcon: false,
        disableClearable: true,
        disabled,
        sx: {
          padding: theme.spacing(0.5, 1),
          borderRadius: theme.shape.borderRadius / 8,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.palette.custom.OV.sys.surface,
          },
          '& .MuiInput-root': {
            cursor: 'pointer',
          },
          '& .MuiInput-input': {
            userSelect: 'none',
            cursor: 'pointer',
            '&::selection': {
              background: 'transparent',
            },
          },
          '&.Mui-focused': {
            backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHigh,
          },
        },
        ListboxProps: {
          sx:
            theme.palette.mode === 'dark'
              ? {
                  background: theme.palette.custom.OV.sys.surfaceContainerHigh,
                  border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
                  borderRadius: theme.shape.borderRadius / 8,
                }
              : {},
        },
      },
      textFieldProps: {
        size: 'small',
      },
      inputProps: {
        sx: {
          ...theme.typography.body2,
          fontSize: theme.utils.important(componentsFontSizes.input),
        },
      },
    }),
    [disabled, theme],
  );

  const associatedSiteFormFields = useMemo(() => {
    const serviceLevelFields = (
      hasServiceLevelAccess
        ? [
            {
              type: 'autocomplete',
              name: 'serviceLevel' as const,
              label: 'Service Level',
              hidden: !isSiteFieldVisible(
                SITE_CARD_PROPERTY_KEYS.serviceLevel,
                !currentServiceLevel,
              ),
              required: 'Service Level is required',
              options: serviceLevelOptions,
              defaultValue: currentServiceLevel,
              ...commonProps,
              renderField: (field: FormAutocompleteProps, formDisabled?: boolean) => (
                <ServiceLevelSelectMenuField
                  field={field}
                  serviceLevelOptions={serviceLevelOptions}
                  cppExpDate={address?.cppExpDate}
                  disabled={formDisabled}
                />
              ),
              autocompleteProps: {
                ...commonProps.autocompleteProps,
                disabled:
                  commonProps.autocompleteProps.disabled ||
                  Boolean(!canUpdateSLA || addressSubscriptionId),
                loading: loadingServiceLevels,
              },
            },
          ]
        : []
    ) as FormAutocompleteProps[];

    const projectManagerField = (
      project
        ? {
            type: 'autocomplete',
            name: 'projectManager' as const,
            label: 'Project Manager',
            required: 'Project Manager is required',
            options: [],
            defaultValue: currentProjectManager ?? null,
            ...commonProps,
            renderField: (field: FormAutocompleteProps, formDisabled?: boolean) => (
              <ProjectManagerSelectMenuField
                field={field}
                partnerId={project?.partnerId ?? address?.partnerId}
                disabled={formDisabled}
              />
            ),
            textFieldProps: {
              ...commonProps.textFieldProps,
              placeholder: 'Assign',
            },
          }
        : {}
    ) as FormAutocompleteProps;

    const accountManagerFields = [
      {
        type: 'autocomplete',
        name: 'accountManager' as const,
        label: 'Account Manager',
        hidden: !isSiteFieldVisible(
          SITE_CARD_PROPERTY_KEYS.accountManager,
          !currentAccountManager,
        ),
        required: 'Account Manager is required',
        options: [],
        defaultValue: currentAccountManager ?? null,
        ...commonProps,
        renderField: (field: FormAutocompleteProps, formDisabled?: boolean) => (
          <AccountManagerSelectMenuField
            field={field}
            partnerId={address?.partnerId}
            disabled={formDisabled}
          />
        ),
        autocompleteProps: {
          ...commonProps.autocompleteProps,
          value: currentAccountManager ? currentAccountManager : null,
        },
        textFieldProps: {
          ...commonProps.textFieldProps,
          placeholder: 'Assign',
        },
      },
    ] as FormAutocompleteProps[];

    if (siteFieldOrder) {
      const siteFieldsByKey: Record<string, FormAutocompleteProps[]> = {
        [SITE_CARD_PROPERTY_KEYS.serviceLevel]: serviceLevelFields,
        [SITE_CARD_PROPERTY_KEYS.accountManager]: accountManagerFields,
      };

      for (const [key, row] of Object.entries(customAttributeRows)) {
        siteFieldsByKey[key] = [
          { type: 'autocomplete', name: key, options: [], renderField: () => row },
        ];
      }

      return [
        ...siteFieldOrder.flatMap((key) => siteFieldsByKey[key] ?? []),
        ...(project ? [projectManagerField] : []),
      ];
    }

    return [...serviceLevelFields, projectManagerField, ...accountManagerFields];
  }, [
    siteFieldOrder,
    customAttributeRows,
    currentServiceLevel,
    serviceLevelOptions,
    address?.cppExpDate,
    address?.partnerId,
    canUpdateSLA,
    addressSubscriptionId,
    loadingServiceLevels,
    commonProps,
    currentAccountManager,
    currentProjectManager,
    project,
    hasServiceLevelAccess,
    isSiteFieldVisible,
  ]);

  return { associatedSiteFormFields, updateAddress, commonProps, updateProject };
}
